import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronDown } from "lucide-react";
import Header from "./landing/Header";
import Footer from "./landing/Footer";
import Seo from "./Seo";

export default function SeoArticlePage({ seo, eyebrow, h1, intro, sections = [], faq = [], related = [], children }) {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [seo.path]);

  const faqJsonLd = faq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid={seo.testid}>
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={faqJsonLd} />
      <Header />
      <main>
        <section className="pt-32 pb-14 bg-gradient-to-b from-brand-softer to-white">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            {eyebrow && <p className="text-brand-limeDark font-semibold mb-3">{eyebrow}</p>}
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight">{h1}</h1>
            {intro && <p className="mt-5 text-lg text-brand-green/70 leading-relaxed">{intro}</p>}
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => navigate("/connexion")} className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-all shadow-md" data-testid="seo-cta-primary">
                Créer mon compte gratuit
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <Link to="/tarifs" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold">
                Voir les tarifs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {children}

        {sections.length > 0 && (
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
            {sections.map((s, i) => (
              <section key={i} className="mt-10 first:mt-0">
                <h2 className="font-display text-2xl lg:text-3xl font-semibold text-brand-greenDeep mb-3">{s.title}</h2>
                {(s.paragraphs || []).map((p, j) => (
                  <p key={j} className="text-brand-green/80 leading-relaxed text-[15px] mb-3">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="list-disc pl-5 space-y-1.5 text-brand-green/80 text-[15px]">
                    {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}

        {faq.length > 0 && (
          <section className="bg-brand-softer py-16">
            <div className="max-w-3xl mx-auto px-5 lg:px-8">
              <h2 className="font-display text-3xl font-semibold text-brand-greenDeep mb-6">Questions fréquentes</h2>
              <div className="space-y-3">
                {faq.map((f, i) => (
                  <details key={i} className="group bg-white rounded-2xl border border-brand-soft p-5" data-testid={`faq-${i}`}>
                    <summary className="flex items-center justify-between cursor-pointer font-semibold text-brand-greenDeep list-none [&::-webkit-details-marker]:hidden">
                      {f.q}
                      <ChevronDown className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="mt-3 text-brand-green/75 text-[15px] leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mb-4">À lire aussi</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="flex items-center justify-between gap-2 px-5 py-4 rounded-2xl border border-brand-soft hover:bg-brand-softer transition-colors text-brand-greenDeep font-medium">
                  {r.label} <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <div className="rounded-3xl bg-brand-greenDeep text-white p-10 text-center">
              <h2 className="font-display text-3xl font-semibold">Prêt à simplifier votre gestion locative ?</h2>
              <p className="mt-3 text-white/75">Gratuit pour 1 bien. Sans carte bancaire, sans engagement.</p>
              <button onClick={() => navigate("/connexion")} className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold" data-testid="seo-cta-footer">
                Créer mon compte gratuit <ArrowUpRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
